import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/**
 * Captures barcode scans from USB / Bluetooth scanners running in HID
 * keyboard-emulation mode ("keyboard wedge") — the default for virtually all
 * retail scanners. The scanner types the payload as keystrokes and usually
 * terminates with Enter, so there is no device API to talk to: we buffer
 * document-level keystrokes and commit on the terminator or on an idle gap.
 *
 * Keystrokes originating from a focused text control are ignored, so a visible
 * input (e.g. the manual-entry fallback) keeps working normally and cannot
 * double-submit alongside this listener.
 */

/** Shortest payload we will treat as a scan, to filter stray keypresses. */
const DEFAULT_MIN_LENGTH = 3;

/**
 * Scanners emit their payload as a fast burst. A gap longer than this ends the
 * buffer, which also commits scans from devices configured with no terminator.
 */
const DEFAULT_IDLE_GAP_MS = 120;

export interface BarcodeWedgeOptions {
  minLength?: number;
  idleGapMs?: number;
}

@Injectable({ providedIn: 'root' })
export class BarcodeWedgeService {
  private readonly scanSubject = new Subject<string>();

  private buffer = '';
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private keydownHandler: ((event: KeyboardEvent) => void) | null = null;
  private minLength = DEFAULT_MIN_LENGTH;
  private idleGapMs = DEFAULT_IDLE_GAP_MS;

  /** Emits a completed scan payload. */
  get scans$(): Observable<string> {
    return this.scanSubject.asObservable();
  }

  get isListening(): boolean {
    return this.keydownHandler !== null;
  }

  start(options?: BarcodeWedgeOptions): void {
    if (typeof document === 'undefined') {
      return;
    }

    // Restart cleanly so a second start() cannot leak a listener or carry over
    // a partial buffer from the previous session.
    this.stop();

    this.minLength = options?.minLength ?? DEFAULT_MIN_LENGTH;
    this.idleGapMs = options?.idleGapMs ?? DEFAULT_IDLE_GAP_MS;

    this.keydownHandler = (event: KeyboardEvent) => this.handleKeydown(event);
    document.addEventListener('keydown', this.keydownHandler, true);
  }

  stop(): void {
    if (this.keydownHandler && typeof document !== 'undefined') {
      document.removeEventListener('keydown', this.keydownHandler, true);
    }
    this.keydownHandler = null;
    this.clearIdleTimer();
    this.buffer = '';
  }

  private handleKeydown(event: KeyboardEvent): void {
    if (this.isFromTextControl(event.target)) {
      return;
    }

    // Shortcuts and chords are never part of a scan payload.
    if (event.ctrlKey || event.metaKey || event.altKey) {
      this.resetBuffer();
      return;
    }

    if (event.key === 'Enter' || event.key === 'Tab') {
      this.commit();
      return;
    }

    if (event.key === 'Escape') {
      this.resetBuffer();
      return;
    }

    // Printable characters report a single-character `key`; ignore Shift, arrows, F-keys, etc.
    if (event.key.length !== 1) {
      return;
    }

    this.buffer += event.key;
    this.restartIdleTimer();
  }

  /** Ignore typing aimed at a real field so the manual-entry input owns its own keystrokes. */
  private isFromTextControl(target: EventTarget | null): boolean {
    const element = target as HTMLElement | null;
    if (!element?.tagName) {
      return false;
    }
    const tagName = element.tagName.toLowerCase();
    return (
      tagName === 'input' ||
      tagName === 'textarea' ||
      tagName === 'select' ||
      element.isContentEditable === true
    );
  }

  private commit(): void {
    const value = this.buffer.trim();
    this.resetBuffer();

    if (value.length >= this.minLength) {
      this.scanSubject.next(value);
    }
  }

  private restartIdleTimer(): void {
    this.clearIdleTimer();
    this.idleTimer = setTimeout(() => {
      this.idleTimer = null;
      this.commit();
    }, this.idleGapMs);
  }

  private resetBuffer(): void {
    this.buffer = '';
    this.clearIdleTimer();
  }

  private clearIdleTimer(): void {
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }
}
